import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import './EmailVerification.css';

interface LocationState {
  email?: string;
  websiteUrl?: string;
}

function EmailVerification() {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const email = state?.email || '';
  const websiteUrl = state?.websiteUrl || '';
  const { login } = useAuth();

  useEffect(() => {
    if (!email) {
      toast.error('Email information is missing. Please start over.');
      navigate('/company');
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }

    if (!/^\d*$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);
    }
  };

  const handleVerify = async () => {
    const otpCode = otp.join('');

    if (otpCode.length !== 6) {
      toast.error('Please enter all 6 digits');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          otp: otpCode,
          websiteUrl
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Email verified successfully!');

        // Save auth token and user info
        if (data.token && data.user) {
          login(data.token, data.user);
        }

        navigate('/thank-you', {
          state: {
            email,
            websiteUrl,
            fromSignup: true
          }
        });
      } else {
        toast.error(data.error || 'Invalid verification code. Please try again.');
        setOtp(['', '', '', '', '', '']);
      }
    } catch (error) {
      console.error('Verification error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('New verification code sent!');
        setResendCooldown(60);
        setOtp(['', '', '', '', '', '']);
      } else {
        toast.error(data.error || 'Failed to resend code. Please try again.');
      }
    } catch (error) {
      console.error('Resend error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="verification-container">
      <div className="verification-box">
        <div className="verification-header">
          <h1>Check Your <span>Email</span></h1>
          <p>We've sent a 6-digit verification code to</p>
          <p className="email-display">{email}</p>
        </div>

        <div className="otp-container">
          <p className="otp-label">Enter 6-digit code</p>
          <div className="otp-inputs">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                pattern="\d*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={isLoading}
                className="otp-input"
                autoFocus={index === 0}
              />
            ))}
          </div>
        </div>

        <button
          onClick={handleVerify}
          className="verify-button"
          disabled={isLoading || otp.join('').length !== 6}
        >
          {isLoading ? 'Verifying...' : 'Verify Email'}
        </button>

        <div className="resend-section">
          <p>Didn't receive the code?</p>
          <button
            onClick={handleResend}
            className="resend-button"
            disabled={isLoading || resendCooldown > 0}
          >
            {resendCooldown > 0
              ? `Resend in ${resendCooldown}s`
              : 'Resend Code'}
          </button>
        </div>

        <div className="verification-footer">
          <p className="security-note">
            This code expires in 5 minutes for your security
          </p>
        </div>
      </div>
    </div>
  );
}

export default EmailVerification;